import { CalendarClock, History, Pause, Pencil, Play, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { TaskSchedule } from "@/api/types";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDestructiveDialog } from "@/components/ui/confirm-destructive-dialog";
import {
  DataTable,
  type DataTableColumn,
} from "@/components/ui/data-table";
import {
  useDeleteTaskSchedule,
  useTaskSchedules,
  useUpdateTaskSchedule,
} from "@/features/scheduler/hooks/useTaskSchedules";
import { describeSchedule } from "@/features/scheduler/lib/recurrence";
import { formatDateTime } from "@/lib/date";

export function SchedulerListPage() {
  const { t } = useTranslation("scheduler");
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useTaskSchedules();
  const [deleteRow, setDeleteRow] = useState<TaskSchedule | null>(null);
  const deleteSchedule = useDeleteTaskSchedule();

  async function handleDelete() {
    if (!deleteRow) return;
    try {
      await deleteSchedule.mutateAsync(deleteRow.id);
      toast.success(t("toasts.deleted"));
      setDeleteRow(null);
    } catch (err) {
      toastApiError(err);
    }
  }

  const columns: DataTableColumn<TaskSchedule>[] = [
    {
      key: "title",
      header: t("table.title"),
      render: (s) => (
        <div className="flex max-w-[260px] flex-col">
          <span className="truncate font-medium">{s.title}</span>
          <span className="truncate text-xs text-ink-500">
            {s.agent_name ?? s.team_session_name ?? ""}
          </span>
        </div>
      ),
    },
    {
      key: "recurrence",
      header: t("table.recurrence"),
      render: (s) => describeSchedule(s, t),
    },
    {
      key: "next",
      header: t("table.nextRun"),
      render: (s) =>
        s.enabled ? formatDateTime(s.next_run_at) : "—",
    },
    {
      key: "enabled",
      header: t("table.state"),
      render: (s) => (
        <Badge variant={s.enabled ? "success" : "secondary"}>
          {s.enabled ? t("state.enabled") : t("state.paused")}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      className: "w-44 text-right",
      render: (s) => <RowActions schedule={s} onDelete={() => setDeleteRow(s)} />,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        description={t("description")}
        actions={
          <Button onClick={() => navigate("/scheduler/new")}>
            <Plus className="h-4 w-4" />
            {t("actions.new")}
          </Button>
        }
      />

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          title={t("empty.title")}
          description={t("empty.description")}
          action={
            <Button onClick={() => navigate("/scheduler/new")}>
              <Plus className="h-4 w-4" />
              {t("actions.new")}
            </Button>
          }
        />
      ) : (
        <DataTable
          columns={columns}
          rows={data}
          getRowKey={(s) => s.id}
          getRowHref={(s) => `/scheduler/${s.id}`}
        />
      )}

      <ConfirmDestructiveDialog
        open={!!deleteRow}
        onOpenChange={(o) => !o && setDeleteRow(null)}
        title={t("deleteDialog.title")}
        description={t("deleteDialog.description")}
        resourceName={deleteRow?.title ?? ""}
        resourceKind={t("deleteDialog.resourceKind")}
        onConfirm={() => void handleDelete()}
        isPending={deleteSchedule.isPending}
      />
    </div>
  );
}

function RowActions({
  schedule,
  onDelete,
}: {
  schedule: TaskSchedule;
  onDelete: () => void;
}) {
  const { t } = useTranslation("scheduler");
  const navigate = useNavigate();
  const update = useUpdateTaskSchedule(schedule.id);

  async function toggle(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      await update.mutateAsync({ enabled: !schedule.enabled });
      toast.success(
        schedule.enabled ? t("toasts.paused") : t("toasts.resumed"),
      );
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <div className="flex items-center justify-end gap-1">
      <Button
        size="icon"
        variant="ghost"
        title={schedule.enabled ? t("actions.pause") : t("actions.resume")}
        aria-label={
          schedule.enabled ? t("actions.pause") : t("actions.resume")
        }
        disabled={update.isPending}
        onClick={(e) => void toggle(e)}
      >
        {schedule.enabled ? (
          <Pause className="h-4 w-4" />
        ) : (
          <Play className="h-4 w-4" />
        )}
      </Button>
      <Button
        size="icon"
        variant="ghost"
        title={t("actions.history")}
        aria-label={t("actions.history")}
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/scheduler/${schedule.id}?tab=history`);
        }}
      >
        <History className="h-4 w-4" />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        title={t("actions.edit")}
        aria-label={t("actions.edit")}
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/scheduler/${schedule.id}`);
        }}
      >
        <Pencil className="h-4 w-4" />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        title={t("actions.delete")}
        aria-label={t("actions.delete")}
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
      >
        <Trash2 className="h-4 w-4 text-danger-500" />
      </Button>
    </div>
  );
}
